import { get, isNil } from 'lodash';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { ContentType } from 'src/app/story/models/content-type.enum';
import { UserRoleModel } from '../user-role.model';
import { UserPermissionEnum } from './user-permission.enum';
import { environment } from 'src/environments/environment';
import { BookmarkModuleAccessorLevel } from 'src/app/bookmarks/models/bookmark-module-accessor-level.enum';

/**
 * Utilities for managing user permissions.
 */
export namespace PermissionsUtil {
  /**
   * Gets if the user has a specific permission.
   * @param role The user's role
   * @param permission The permission to check
   * @returns If the user has the permission
   */
  export function hasPermission(role: UserRoleModel, permission: UserPermissionEnum): boolean {
    const userPermissions: UserPermissionEnum[] = get(role, 'permissions', []);
    return userPermissions.includes(permission);
  }

  /**
   * Gets if the user can edit the bookmark in Studio/Loft.
   * @param userRole The user's role
   * @param bookmark The bookmark
   * @param isPremiumUser If the user is premium
   * @returns Can the user edit the bookmark in Studio
   */
  export function canEditInStudio(
    userRole: UserRoleModel,
    bookmark: BookmarkModel,
    isPremiumUser: boolean,
  ): boolean {
    const canEditBookmarks = PermissionsUtil.hasPermission(
      userRole,
      UserPermissionEnum.EditBookmarks,
    );

    if (canEditBookmarks && !environment.native) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Gets if a user's role has access to edit the bookmark.
   * @param role The user's role
   * @param bookmark The bookmark
   * @returns Can the user edit the bookmark
   */
  export function canEditBookmark(role: UserRoleModel, bookmark: BookmarkModel): boolean {
    const canEditBookmarks = hasPermission(role, UserPermissionEnum.EditBookmarks);
    const isBookmarkOwner = bookmark.is_owner === true;

    // cannot edit any bookmarks
    if (!canEditBookmarks) {
      return false;
    }

    // can edit explore content
    if (bookmark.type === ContentType.Module) {
      return canEditBookmarks;
    }

    // can edit own bookmark
    if (isBookmarkOwner) {
      return true;
    }

    // can edit team bookmarks
    if (isGroupBookmark(bookmark)) {
      return canEditTeamBookmark(role, bookmark);
    }

    // default: no permission
    return false;
  }

  export function canViewBookmarkInStudio(role: UserRoleModel, bookmark: BookmarkModel): boolean {
    // TODO: future support to reject access to specific model
    return true;
  }

  export function canDeleteBookmark(role: UserRoleModel, bookmark: BookmarkModel): boolean {
    const isBookmarkOwner = bookmark.is_owner === true;

    // only owners can delete bookmarks
    return isBookmarkOwner;
  }

  export function canManageBookmark(role: UserRoleModel, bookmark: BookmarkModel): boolean {
    // check can edit bookmark permission
    const canEditBookmarks = hasPermission(role, UserPermissionEnum.EditBookmarks);

    // cannot edit any bookmarks
    if (!canEditBookmarks) {
      return false;
    }

    // can edit own bookmark
    const isBookmarkOwner = bookmark.is_owner === true;
    if (isBookmarkOwner) {
      return true;
    }

    if (isGroupModule(bookmark)) {
      // can manage custom modules
      return true;
    } else if (isGroupBookmark(bookmark)) {
      // can edit if team editable
      return canEditTeamBookmark(role, bookmark);
    }

    // default
    return false;
  }

  function canEditTeamBookmark(role: UserRoleModel, bookmark: BookmarkModel): boolean {
    const canEditAllTeamBookmarks = hasPermission(role, UserPermissionEnum.EditAllTeamBookmarks);

    if (canEditAllTeamBookmarks) {
      return true;
    }

    // can edit team bookmarks (w/ explicit Edit Access)
    const canEditTeamBookmarks = hasPermission(role, UserPermissionEnum.EditTeamBookmarks);
    const bookmarkEditableByTeam =
      isGroupBookmark(bookmark) && bookmark.group_access[0].member_edit;
    return canEditTeamBookmarks && bookmarkEditableByTeam;
  }

  export function canCopyURL(
    role: UserRoleModel,
    bookmark: BookmarkModel,
    isPremiumUser: boolean,
    isTeamUser: boolean,
  ): boolean {
    // requires premium access
    if (!isPremiumUser) {
      return false;
    }

    // requires team access
    if (!isTeamUser) {
      return false;
    }

    // check permissions
    return hasPermission(role, UserPermissionEnum.CopyUrl);
  }

  export function canPublishBookmark(role: UserRoleModel, bookmark: BookmarkModel): boolean {
    const canPublishBookmarks = hasPermission(role, UserPermissionEnum.PublishBookmarks);

    // requires permission
    return canPublishBookmarks;
  }

  export function canAddToMyLibrary(
    role: UserRoleModel,
    bookmark: BookmarkModel,
    isPremiumUser: boolean,
  ) {
    const isCustomUserModel = !isNil(bookmark.module_accessor_level);

    // exclude custom models
    if (isCustomUserModel) {
      return false;
    }

    // default
    return true;
  }

  export function canExportCSV(role: UserRoleModel, isPremiumUser: boolean): boolean {
    // requires premium access
    if (!isPremiumUser) {
      return false;
    }

    // check permissions
    return hasPermission(role, UserPermissionEnum.ExportCSV);
  }

  export function isGroupModule(bookmark: BookmarkModel): boolean {
    return (
      bookmark.type == ContentType.Module &&
      bookmark.module_accessor_level === BookmarkModuleAccessorLevel.Group
    );
  }

  export function isGroupBookmark(bookmark: BookmarkModel): boolean {
    return Array.isArray(bookmark.group_access) && bookmark.group_access.length > 0;
  }
}
